import { bffApiClient } from '@api/bff/client';
import { getIsOnline } from './connectivityService';

type ErrorReport = {
  title: string;
  message?: string;
  stackTrace?: string;
  metadata?: Record<string, unknown>;
};

function reportError(report: ErrorReport): void {
  if (!getIsOnline()) return;

  // Fire-and-forget through our own API proxy (avoids CORS, keeps API key server-side)
  bffApiClient.post('/api/error-inbox', report).catch(() => {}); // swallow - don't let error reporting cause errors
}

/** Report a 500+ API error from an axios response */
export function reportApiError(error: {
  response?: { status?: number; data?: unknown };
  config?: { method?: string; url?: string };
  message?: string;
}): void {
  const status = error.response?.status;
  if (!status || status < 500) return;

  const method = (error.config?.method ?? 'UNKNOWN').toUpperCase();
  const url = error.config?.url ?? 'unknown';

  const serverMessage =
    (error.response?.data as { message?: string })?.message ??
    (error.response?.data as { error?: string })?.error ??
    error.message;

  reportError({
    title: `${status} ${method} ${url}`,
    message: serverMessage,
    metadata: { type: 'api_error', statusCode: status, method, url }
  });
}

/** Report a sync queue item that permanently failed */
export function reportSyncFailure(item: {
  entityType: string;
  entityKey: string;
  retryCount: number;
  lastError?: string;
  payload?: unknown;
}): void {
  reportError({
    title: `Sync failed: ${item.entityType} (${item.entityKey})`,
    message: item.lastError,
    metadata: {
      type: 'sync_failure',
      entityType: item.entityType,
      entityKey: item.entityKey,
      retryCount: item.retryCount
    }
  });
}

/** Report a failed calibration completion attempt */
export function reportCalibrationFailure(context: {
  calibrationRuntimeId: string;
  calibrationId: string;
  jobNumber: string;
  sequenceNumber: string;
  error?: unknown;
}): void {
  const message =
    context.error instanceof Error
      ? context.error.message
      : context.error
        ? String(context.error)
        : undefined;

  reportError({
    title: `Calibration completion failed: ${context.jobNumber}-${context.sequenceNumber}`,
    message,
    stackTrace:
      context.error instanceof Error ? context.error.stack : undefined,
    metadata: {
      type: 'calibration_failure',
      calibrationRuntimeId: context.calibrationRuntimeId,
      calibrationId: context.calibrationId,
      jobNumber: context.jobNumber,
      sequenceNumber: context.sequenceNumber
    }
  });
}
